import { useState } from 'react';
import { Box } from '@gemini/core';
import { IFieldOptions, IFieldProps, IFieldValues } from '@mfe-types/field';

const getCurrentSelectionConfig = (value: string, options: IFieldOptions['values']) => {
    return options.find((option) => option.value === value);
};

const PropertyRating = ({ field, options, texts }: IFieldProps<any, any>) => {
    const { value: defaultValue, onChange } = field;
    const [selectedValue, setSelectedValue] = useState<IFieldValues | undefined>(
        getCurrentSelectionConfig(defaultValue, options.values),
    );
    const handleChange = (e: React.MouseEvent<HTMLButtonElement>, value: string | undefined) => {
        e.preventDefault();
        if (!value) return;
        onChange(value);
        setSelectedValue(getCurrentSelectionConfig(value, options.values));
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
        const currentValueIndex = options.values.findIndex(
            (option) => option.value === selectedValue?.value,
        );
        const maxIndex = options.values.length - 1;
        if (e.key === 'ArrowLeft' || e.code === 'ArrowLeft') {
            const newSelectedValueIndex =
                currentValueIndex === 0 ? maxIndex : currentValueIndex - 1;
            onChange(options.values[newSelectedValueIndex].value);
            setSelectedValue(options.values[newSelectedValueIndex]);
        } else if (e.key === 'ArrowRight' || e.code === 'ArrowRight') {
            const newSelectedValueIndex =
                currentValueIndex === maxIndex ? 0 : currentValueIndex + 1;
            onChange(options.values[newSelectedValueIndex].value);
            setSelectedValue(options.values[newSelectedValueIndex]);
        } else if (e.key === 'Enter' || e.code === 'Enter' || e.key === ' ' || e.code === 'Space') {
            e.preventDefault();
        }
    };

    if (!options?.values) return null;

    return (
        <>
            <Box as="label" paddingVertical="spacing.4" typography="typography.body.14.bold">
                {texts.label}
            </Box>
            <Box
                testId={field.name}
                marginTop="spacing.8"
                padding="spacing.16"
                borderWidth="borderWidth.1"
                borderStyle="solid"
                borderColor="color.border.light.default"
                borderRadius="radius.16"
            >
                <Box
                    testId="radio-group"
                    aria-label={texts.ariaLabel?.text}
                    aria-describedby={`${field.name}-description`}
                    display="flex"
                    flexDirection="row"
                    justifyContent="center"
                    gap="spacing.8"
                >
                    {options.values.map((option: IFieldValues) => (
                        <Box
                            as="button"
                            onClick={(e) => handleChange(e, option.value)}
                            onKeyDown={handleKeyDown}
                            testId={`${field.name}${option.value}Button`}
                            key={option.value}
                            aria-label={option.title}
                            width="sizing.40"
                            height="sizing.40"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            cursor="pointer"
                            borderRadius="radius.4"
                            borderStyle="solid"
                            borderWidth={
                                option.value === selectedValue?.value
                                    ? 'borderWidth.2'
                                    : 'borderWidth.1'
                            }
                            borderColor={
                                option.value === selectedValue?.value
                                    ? 'color.border.interactive.default'
                                    : 'color.border.subdued.default'
                            }
                            outlineWidth={
                                option.value === selectedValue?.value
                                    ? 'borderWidth.2'
                                    : 'borderWidth.none'
                            }
                            outlineColor={
                                option.value === selectedValue?.value
                                    ? 'color.border.interactive.default'
                                    : 'color.surface.subdued.default'
                            }
                            tabIndex={option.value === selectedValue?.value ? 0 : -1}
                        >
                            <Box as="span" aria-label={`Icon ${option.title}`}>
                                {option.icon}
                            </Box>
                        </Box>
                    ))}
                </Box>
                {selectedValue ? (
                    <Box as="div" testId="radio-group-description" id={`${field.name}-description`}>
                        <Box
                            as="p"
                            typography="typography.body.16.regular"
                            textAlign="center"
                            margin="spacing.none"
                            marginTop="spacing.16"
                            color="color.content.default.default"
                        >
                            {selectedValue.title}
                        </Box>
                        <Box
                            as="p"
                            typography="typography.body.14.regular"
                            textAlign="center"
                            marginTop="spacing.4"
                            color="color.content.light.default"
                        >
                            {selectedValue.additionalText}
                        </Box>
                    </Box>
                ) : null}
            </Box>
        </>
    );
};

export default PropertyRating;
